import { Injectable } from '@angular/core';

/**
 * Interface for HubSpot deal update object
 */
export interface HubspotDealUpdate {
  idTrosten: string;
  aliasDestino: string;
  aliasOrigen: string;
  idNegocioHubSpot: string;
  nombreContratante: string;
  nombreCliente: string;
  total: number;
  fechaInicio: string;
  fechaFin?: string;
}

/**
 * Service for formatting data for HubSpot API integrations
 *
 * Handles data transformation and formatting required by HubSpot's API,
 * including deal creation and updates.
 *
 * @example
 * constructor(private hubspotFormatter: HubspotFormatterService) {}
 *
 * const dealData = this.hubspotFormatter.formatConfirmacionForHubspot(
 *   confirmacion,
 *   servicio,
 *   cliente,
 *   contratante
 * );
 */
@Injectable()
export class HubspotFormatterService {

  constructor() { }

  /**
   * Formats confirmacion data for HubSpot deal update
   *
   * @param confirmacion - Confirmacion object
   * @param servicio - Service object with dates and location info
   * @param cliente - Cliente object
   * @param nombreContratante - Name of the contratante
   * @param fechaInicio - Formatted start date (ISO midnight format)
   * @param fechaFin - Formatted end date (ISO midnight format)
   * @returns HubspotDealUpdate object ready for API call
   */
  formatConfirmacionForHubspot(
    confirmacion: any,
    servicio: any,
    cliente: any,
    nombreContratante: string,
    fechaInicio: string,
    fechaFin?: string
  ): HubspotDealUpdate {
    const dealUpdate: HubspotDealUpdate = {
      idTrosten: confirmacion.idTrosten,
      aliasDestino: servicio.destino && servicio.destino.alias || '',
      aliasOrigen: servicio.origen && servicio.origen.alias || '',
      idNegocioHubSpot: confirmacion.idNegocioHubSpot,
      nombreContratante: nombreContratante,
      nombreCliente: cliente.nombre,
      total: confirmacion.total,
      fechaInicio: fechaInicio
    };

    if (fechaFin) {
      dealUpdate.fechaFin = fechaFin;
    }

    return dealUpdate;
  }

  /**
   * Formats cliente info for HubSpot deal creation
   *
   * @param cliente - Cliente object
   * @param confirmacion - Confirmacion object (for punto origen)
   * @returns Object with cliente information formatted for HubSpot
   */
  formatClienteInfoForHubspot(cliente: any, confirmacion: any): {
    nombre: string,
    telefono: string,
    email: string,
    numeroDeCliente: string,
    metodoDeCotizacion: string,
    idCliente: string,
    puntoOrigen: string
  } {
    return {
      nombre: cliente.nombre || '',
      telefono: cliente.telefono || '',
      email: cliente.email || '',
      numeroDeCliente: cliente.numeroDeCliente || '',
      metodoDeCotizacion: cliente.metodoDeCotizacion || '',
      idCliente: cliente.$key || '',
      puntoOrigen: confirmacion.origen && confirmacion.origen.alias || ''
    };
  }

  /**
   * Calculates total amount from legacy cotizacion/confirmacion structure
   *
   * @param cotizacionOConfirmacion - Cotizacion or confirmacion object
   * @returns Total amount calculated from precioCombinaciones or combinacion
   */
  calculateTotalFromLegacyStructure(cotizacionOConfirmacion: any): number {
    let servicio: any = {};

    // Extract first service from servicios object
    for (const idServicio in cotizacionOConfirmacion.servicios) {
      if (cotizacionOConfirmacion.servicios.hasOwnProperty(idServicio)) {
        servicio = cotizacionOConfirmacion.servicios[idServicio];
        break;
      }
    }

    let totalCotizado = 0;

    if (servicio.precioCombinaciones) {
      for (const idCombinacion in servicio.precioCombinaciones) {
        if (servicio.precioCombinaciones.hasOwnProperty(idCombinacion)) {
          totalCotizado = servicio.precioCombinaciones[idCombinacion].total || 0;
          break;
        }
      }
    } else if (servicio.combinacion && servicio.combinacion.precio) {
      totalCotizado = servicio.combinacion.precio;
    }

    return totalCotizado;
  }

  /**
   * Extracts servicio info from legacy structure for HubSpot
   *
   * @param cotizacionOConfirmacion - Cotizacion or confirmacion object
   * @returns Service info object with dates and details
   */
  extractServicioInfoFromLegacy(cotizacionOConfirmacion: any): any {
    for (const idServicio in cotizacionOConfirmacion.servicios) {
      if (cotizacionOConfirmacion.servicios.hasOwnProperty(idServicio)) {
        return cotizacionOConfirmacion.servicios[idServicio];
      }
    }
    return {};
  }
}



// WEBPACK FOOTER //
// E:/Github/trosten/trosten-web-app/src/app/services/integration/hubspot/hubspot-formatter.service.ts